import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardCodes,
    KeyboardSensor,
    PointerSensor,
    UniqueIdentifier,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSSProperties, useMemo } from 'react';

type SortableDndContainerProps = {
    isDisabled: boolean;
    internalDragAndDropElements: Array<(shouldApplyPlaceholder?: boolean, activeId?: string) => React.ReactNode>;
    internalDragAndDropElementsValues: Array<UniqueIdentifier>;
    styles?: CSSProperties;
    useDragOverlay?: boolean;
    handleDragEnd: (event: DragEndEvent) => void;
    activeId: any;
    setActiveId: (activeId: string) => void;
}

const defaultKeyboardCodes: KeyboardCodes = {
    start: [],
    cancel: ['Escape'],
    end: ['Enter']
};

export const SortableDndContainer = ({
    isDisabled, useDragOverlay = false, internalDragAndDropElements,
    internalDragAndDropElementsValues, styles,
    handleDragEnd, activeId, setActiveId }: SortableDndContainerProps) => {

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 0.01 },
        }),
        useSensor(KeyboardSensor, {
            keyboardCodes: defaultKeyboardCodes,
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(`${event.active.id}`);
    };

    const handleDragEndWithOverlay = (event: DragEndEvent) => {
        handleDragEnd(event);
        setActiveId(null);
    };

    const dragAndDropElements = useMemo(() => {
        return internalDragAndDropElements.map(element => element(true, activeId));
    }, [internalDragAndDropElements, activeId]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEndWithOverlay}
        >
            <SortableContext
                items={internalDragAndDropElementsValues}
                strategy={verticalListSortingStrategy}
                disabled={isDisabled}
            >
                <div style={{ position: 'relative', overflow: 'hidden', ...styles }}>
                    {dragAndDropElements}
                </div>
            </SortableContext>
            { /** @ts-ignore */}
            {useDragOverlay && (<DragOverlay>
                {activeId ? (
                    <div style={{ backgroundColor: "white" }}>
                        {internalDragAndDropElements[internalDragAndDropElementsValues.findIndex((item) => item === activeId)](false)}
                    </div>
                ) : null}
            </DragOverlay>)}
        </DndContext>
    );
};
